import React, { useEffect } from 'react';
import { FormContainer, InputBodyContainer } from '../../NewGame.styles';
import ItemSelectInput from '../../custom-inputs/ItemSelectInput';
import HitQuotaAndPlacedInputs from './rewards-inputs/HitQuotaAndPlacedInputs';
import CompanyActionInputs from './rewards-inputs/CompanyActionInputs';
import IfPlacedInputs from './rewards-inputs/IfPlacedInputs';
import NewGameState from '../../_types/NewGameState';
import RewardType from '../../_types/RewardType';
import featureFlag from '../../../../../common/featureFlag';
import { SectionTitle } from '../../GameFormComponents';

interface Props {
  rewards: RewardType[];
  newGameState: NewGameState;
  onChangeNewGameState: (newGameState: NewGameState) => void;
}

const RewardsView: React.FC<Props> = ({
  rewards,
  newGameState,
  onChangeNewGameState,
}) => {
  const isIncentive = newGameState.contestType === 'incentive';

  useEffect(() => {
    if (!isIncentive) return;

    const rewardCurrency = newGameState.rewardCurrency || 'reward';
    const desiredRewardWhen = 'goal';
    const desiredRewardType = `goal_${rewardCurrency}`;

    if (
      newGameState.rewardWhen !== desiredRewardWhen ||
      newGameState.rewardType !== desiredRewardType
    ) {
      onChangeNewGameState({
        ...newGameState,
        rewardWhen: desiredRewardWhen,
        rewardType: desiredRewardType,
      });
    }
  }, [
    isIncentive,
    newGameState.rewardWhen,
    newGameState.rewardCurrency,
    newGameState.rewardType,
  ]);
  // Determine available reward rule options based on feature flags
  const getRewardRuleItems = () => {
    let baseItems: { label: string; value: string; icon: string }[] = [];
    if (newGameState.contestType === 'incentive') {
      baseItems = [
        {
          label: 'If KPI goal is met',
          value: 'goal',
          icon: 'flag',
        },
      ];
    } else {
      baseItems = [
        {
          label: 'If placed (ex 1st,2nd)',
          value: 'mission',
          icon: 'medal',
        },
        {
          label: 'If KPI goal is met',
          value: 'goal',
          icon: 'flag',
        },
      ];

      // Add Hit Quota and Placed only if feature flag is enabled
      if (featureFlag('hit_quota_and_placed_rule')) {
        baseItems.splice(1, 0, {
          label: 'Hit Quota and Placed',
          value: 'mission_min',
          icon: 'flag',
        });
      }

      // Add For every action only if feature flag is enabled
      if (featureFlag('for_every_action_rule')) {
        baseItems.push({
          label: 'For every action (ex. $1/dial)',
          value: 'action',
          icon: 'repeat',
        });
      }
    }

    return baseItems;
  };

  return (
    <FormContainer>
      <SectionTitle>Rewards</SectionTitle>
      <InputBodyContainer>
        <ItemSelectInput
          label="Reward Type"
          selectedItem={newGameState.rewardCurrency}
          onSelect={(newSelectedItem: { label: string; value: any } | null) => {
            if (!newSelectedItem) return;
            const rewardCurrency = newSelectedItem.value;
            // Create the composite rewardType by combining rewardWhen and rewardCurrency
            const rewardType = `${newGameState.rewardWhen}_${rewardCurrency}`;

            onChangeNewGameState({
              ...newGameState,
              rewardCurrency,
              rewardType,
            });
          }}
          items={[
            {
              label: 'Reward store item',
              value: 'reward',
              icon: 'shoppingBag',
            },
            { label: 'Coins', value: 'points', icon: 'coins' },
          ]}
          numColumns={2}
        />
        <ItemSelectInput
          label="Rules"
          selectedItem={newGameState.rewardWhen}
          onSelect={(newSelectedItem: { label: string; value: any } | null) => {
            if (!newSelectedItem) return;
            const rewardWhen = newSelectedItem.value;
            if (isIncentive && rewardWhen !== 'goal') {
              return;
            }
            const rewardType = `${rewardWhen}_${newGameState.rewardCurrency}`;

            onChangeNewGameState({
              ...newGameState,
              rewardWhen,
              rewardType,
            });
          }}
          items={getRewardRuleItems()}
          numColumns={2}
          allowDeselect={false}
        />
        {isIncentive && (
          <div
            style={{
              marginTop: 12,
              fontSize: 13,
              fontWeight: 600,
              color: '#6F878F',
            }}
          >
            Reward rule is fixed to &quot;If KPI goal is met&quot; for incentive
            games.
          </div>
        )}
        <IfPlacedInputs
          rewards={rewards}
          newGameState={newGameState}
          onChangeNewGameState={onChangeNewGameState}
        />
        {/* Only render HitQuotaAndPlacedInputs if the feature flag is enabled */}
        {featureFlag('hit_quota_and_placed_rule') && (
          <HitQuotaAndPlacedInputs
            rewards={rewards}
            newGameState={newGameState}
            onChangeNewGameState={onChangeNewGameState}
          />
        )}
        <CompanyActionInputs
          rewards={rewards}
          newGameState={newGameState}
          onChangeNewGameState={onChangeNewGameState}
        />
      </InputBodyContainer>
    </FormContainer>
  );
};

export default RewardsView;
